














import {
  computed,
  defineComponent,
  inject,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  toRef,
  watch,
} from "@vue/composition-api";
import { Call, CallStatus } from "@scrile/api-provider/dist/api/CallsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { $t } from "@/lib/i18nHelpers";
import { toTimePassed } from "@/lib/time";
import { resolveUserName } from "@/lib/userHelpers";
import { PROVIDED_DATA, PROVIDED_DATA_KEY } from "@/const";
import BaseIcon from "@/components/Base/BaseIcon";
import FormatMoney from "@/components/FormatMoney";

export default defineComponent({
  name: "CallStatusView",
  components: { BaseIcon, FormatMoney },
  props: {
    user: { type: Object as PropType<UserPublicData>, required: true },
    call: { type: Object as PropType<Call | null> },
    connectionError: { type: Boolean, required: true },
  },
  setup(props) {
    const { authUserStrict } = useAuthUser();
    const callPrice = inject<PROVIDED_DATA[PROVIDED_DATA_KEY.PAID_CALL_PRICE]>(
      PROVIDED_DATA_KEY.PAID_CALL_PRICE,
      ref(0)
    );
    const moneyCount = ref(0);
    const time = ref<number | null>(null);
    let interval: number | ReturnType<typeof setTimeout> = 0;
    let watcher = () => {};

    const showMoneyCount = computed(
      () => (moneyCount.value > 0 && props.call && props.call.receiverId === authUserStrict.value.id) || false
    );
    const callStatus = computed(() => (props.call ? props.call.status : CallStatus.NEW));
    const screenName = computed(() => resolveUserName(props.user));
    const callEnded = computed(() =>
      [CallStatus.DECLINED, CallStatus.CANCELLED, CallStatus.ENDED].includes(callStatus.value)
    );

    const description = computed(() => {
      if (props.connectionError) return "";
      if (time.value !== null) {
        return toTimePassed(time.value * 1000);
      }
      if (callEnded.value) return $t("Call ended");
      return "";
    });

    const startTimer = () => {
      if (interval) return;
      time.value = 0;
      interval = setInterval(() => {
        if (time.value !== null) time.value += 1;
      }, 1000);
    };

    const stopTimer = () => {
      if (typeof interval === "number") {
        clearInterval(interval);
      } else clearInterval(interval);
      interval = 0;
      time.value = null;
    };

    watch(
      callStatus,
      status => {
        if (status === CallStatus.ACCEPTED) startTimer();
        else if (callEnded.value) stopTimer();
      },
      { immediate: true }
    );

    onMounted(() => {
      let interval: number;
      watcher = watch(
        toRef(props, "call"),
        call => {
          interval && clearInterval(interval);
          if (call && call.status === CallStatus.ACCEPTED) {
            interval = setInterval(() => {
              const timePassed = new Date().getTime() - new Date(call.startTime || "").getTime();
              moneyCount.value = (Math.floor(timePassed / 1000 / 60) + 1) * callPrice.value;
            }, 1000) as any as number;
          }
        },
        { immediate: true }
      );
    });

    onUnmounted(() => {
      watcher();
      stopTimer();
    });

    return {
      screenName,
      description,
      showMoneyCount,
      moneyCount,
      callPrice,
    };
  },
});
